import React, { useEffect, useState } from "react";
import { AlertCircle, Loader2, QrCode } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import pixService from "@/services/pixService";
import paymentService from "@/services/paymentService";

const EMPTY_FORM = {
  amount: "",
  description: "",
  name: "",
  contact: "",
  order_code: "",
  pay_method: "PIX",
};

const FieldError = ({ message }) =>
  message ? (
    <p className="flex items-center gap-1 text-xs text-destructive">
      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
      {message}
    </p>
  ) : null;

/** "45,00", "1.234,50" ou "45.5" → número */
const parseAmount = (value) => {
  const text = String(value ?? "").trim();
  const normalized = text.includes(",")
    ? text.replace(/\./g, "").replace(",", ".")
    : text;
  return Number(normalized);
};

/**
 * Modal "Novo PIX" (POST /pix/create): gera o PIX Copia e Cola e o QR Code
 * do valor informado. Com outra forma de pagamento só registra a cobrança.
 *
 * Props: `open`, `onOpenChange`, `onSave(payload)`, `loading`.
 */
export default function PixFormModal({ open, onOpenChange, onSave, loading }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [payMethods, setPayMethods] = useState([]);

  useEffect(() => {
    if (!open) return;
    setFormData(EMPTY_FORM);
    setErrors({});

    // Formas de pagamento cadastradas em /payments
    paymentService
      .getNames()
      .then((names) => setPayMethods(Array.isArray(names) ? names : []))
      .catch(() => setPayMethods([]));
  }, [open]);

  // "PIX" sempre disponível como opção
  const payMethodOptions = payMethods.some((name) => name.toLowerCase() === "pix")
    ? payMethods
    : ["PIX", ...payMethods];

  const isPix = formData.pay_method.trim().toLowerCase() === "pix";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const amount = parseAmount(formData.amount);
    const newErrors = pixService.validatePixData({ ...formData, amount });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const payload = {
      amount: Math.round(amount * 100) / 100,
      pay_method: formData.pay_method.trim(),
    };
    ["description", "name", "contact"].forEach((field) => {
      const value = formData[field].trim();
      if (value) payload[field] = value;
    });
    const orderCode = formData.order_code.trim().replace(/^#/, "");
    if (orderCode) payload.order_code = orderCode;

    onSave(payload);
  };

  const field = (name, label, props = {}) => (
    <div className={cn("space-y-1.5", props.className)}>
      <Label htmlFor={`pix-${name}`} className="text-xs font-semibold">
        {label}
        {props.required && <span className="text-destructive"> *</span>}
      </Label>
      <Input
        id={`pix-${name}`}
        inputMode={props.inputMode}
        autoComplete="off"
        placeholder={props.placeholder}
        value={formData[name]}
        onChange={(e) => handleChange(name, e.target.value)}
        className={cn("h-10 rounded-xl bg-surface", errors[name] && "border-destructive")}
        disabled={loading}
        aria-invalid={Boolean(errors[name])}
      />
      <FieldError message={errors[name]} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] max-w-md gap-4 overflow-y-auto rounded-2xl p-5">
        <DialogHeader className="flex-row items-center gap-2 space-y-0 pr-8 text-left">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <QrCode className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="font-display text-lg text-primary">Novo PIX</DialogTitle>
            <DialogDescription className="text-xs">
              Gere QR Code e Copia e Cola instantâneo
            </DialogDescription>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <div className="grid grid-cols-2 gap-2">
            {field("amount", "Valor (R$)", {
              required: true,
              inputMode: "decimal",
              placeholder: "0,00",
            })}
            <div className="space-y-1.5">
              <Label htmlFor="pix-pay_method" className="text-xs font-semibold">
                Forma <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.pay_method}
                onValueChange={(value) => handleChange("pay_method", value)}
                disabled={loading}
              >
                <SelectTrigger
                  id="pix-pay_method"
                  className={cn("h-10 rounded-xl bg-surface", errors.pay_method && "border-destructive")}
                >
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {payMethodOptions.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError message={errors.pay_method} />
            </div>
          </div>

          {!isPix && (
            <p className="rounded-lg bg-terra-soft px-3 py-2 text-xs text-terra">
              Com forma diferente de PIX, a cobrança é registrada sem gerar QR Code.
            </p>
          )}

          {field("description", "Descrição", {
            placeholder: "Ex.: Pedido Balcão",
          })}

          <div className="grid grid-cols-2 gap-2">
            {field("name", "Nome do pagador", { placeholder: "Ex.: João Silva" })}
            {field("contact", "Telefone/WhatsApp", {
              inputMode: "tel",
              placeholder: "Ex.: 11987654321",
            })}
          </div>

          {field("order_code", "Código do pedido (opcional)", {
            placeholder: "Ex.: 298940",
          })}
          <p className="text-[11px] leading-tight text-muted-foreground">
            Sem pedido vinculado, a confirmação do pagamento não altera o status
            de nenhum pedido.
          </p>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="h-10 rounded-full bg-surface-container px-4 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95 disabled:opacity-70"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Gerando..." : isPix ? "Gerar PIX" : "Registrar"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
